import React, { useState, useEffect, useRef, useCallback } from 'react';

import { SignupContinueData } from '../types';

// Types
interface UseSignupScreen3Props {
  handleContinue: (dataObj: SignupContinueData) => void;
}

interface UseSignupScreen3Return {
  // UI state
  scrolled: boolean;
  setScrolled: (scrolled: boolean) => void;
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;

  // Handlers
  handleContinue: () => void;
  handleTermsAcceptance: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleScroll: () => void;
  scroll: (height: number) => void;

  // Computed state
  isLoading: boolean;

  // Refs
  scrollableRef: React.RefObject<HTMLDivElement>;
  buttonTriggerRef: React.RefObject<HTMLParagraphElement>;
}

export const useSignupScreen3 = ({
  handleContinue: handleContinueProp,
}: UseSignupScreen3Props): UseSignupScreen3Return => {
  const [scrolled, setScrolled] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Refs
  const scrollableRef = useRef<HTMLDivElement>(null);
  const buttonTriggerRef = useRef<HTMLParagraphElement>(null);

  // Keyboard navigation for scrollable area
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const scrollableElement = scrollableRef.current;
      if (scrollableElement && scrollableElement.contains(document.activeElement)) {
        if (event.code === 'ArrowDown') {
          event.preventDefault();
          scroll(20);
        } else if (event.code === 'ArrowUp') {
          event.preventDefault();
          scroll(-20);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scroll = useCallback((height: number) => {
    const el = scrollableRef.current;
    if (el) {
      el.scrollBy({
        top: height,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const trigger = buttonTriggerRef.current;
    if (trigger) {
      const box = trigger.getBoundingClientRect();
      if (window.innerHeight / 2 > box.top) {
        setScrolled(true);
      }
    }
  }, []);

  const handleContinue = useCallback(() => {
    if (termsAccepted) {
      const dataObj = {
        progress: 'screen3',
        accepted: 'accepted',
      };
      handleContinueProp(dataObj);
    }
  }, [termsAccepted, handleContinueProp]);

  const handleTermsAcceptance = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);
  }, []);

  return {
    // UI state
    scrolled,
    setScrolled,
    termsAccepted,
    setTermsAccepted,

    // Handlers
    handleContinue,
    handleTermsAcceptance,
    handleScroll,
    scroll,

    // Computed state
    isLoading: false,

    // Refs
    scrollableRef,
    buttonTriggerRef,
  };
};
